/**
 * Action builders for room members and activity logs.
 */
import {
  addRoomMember,
  getRoomActivityLog,
  removeRoomMember,
  updateMemberRole,
} from '../services/firebase/persistentRooms';
import type { RoomRole } from '../types/room';
import type { RoomStoreGet, RoomStoreSet } from './roomStoreTypes';

/**
 * Builds the add member action.
 */
export function addMemberAction(set: RoomStoreSet, get: RoomStoreGet) {
  return async (
    roomId: string,
    email: string,
    userId: string,
    role: RoomRole = 'member'
  ): Promise<void> => {
    set({ error: null });

    try {
      await addRoomMember(roomId, email, userId, role);
      await get().loadRoom(roomId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add member';
      set({ error: message });
      throw error;
    }
  };
}

/**
 * Builds the remove member action.
 */
export function removeMemberAction(set: RoomStoreSet, get: RoomStoreGet) {
  return async (roomId: string, userId: string): Promise<void> => {
    set({ error: null });

    try {
      await removeRoomMember(roomId, userId);
      await get().loadRoom(roomId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove member';
      set({ error: message });
      throw error;
    }
  };
}

/**
 * Builds the change member role action.
 */
export function changeMemberRoleAction(set: RoomStoreSet, get: RoomStoreGet) {
  return async (roomId: string, userId: string, role: RoomRole): Promise<void> => {
    set({ error: null });

    try {
      await updateMemberRole(roomId, userId, role);
      await get().loadRoom(roomId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to change role';
      set({ error: message });
      throw error;
    }
  };
}

/**
 * Builds the get activity log action.
 */
export function getActivityLogAction(set: RoomStoreSet) {
  return async (roomId: string): Promise<Awaited<ReturnType<typeof getRoomActivityLog>>> => {
    try {
      return await getRoomActivityLog(roomId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load activity log';
      set({ error: message });
      return [];
    }
  };
}

/**
 * Builds the clear current room action.
 */
export function clearCurrentRoomAction(set: RoomStoreSet) {
  return (): void => {
    set({ currentRoom: null });
  };
}

/**
 * Builds the clear error action.
 */
export function clearErrorAction(set: RoomStoreSet) {
  return (): void => {
    set({ error: null });
  };
}

